import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  WritableSignal,
  signal,
  viewChild,
} from '@angular/core';
import { FieldComponent } from '@appkit4/angular-components/field';
import { DatepickerOnDateInvalid } from '@shared/abstraction';
import { AppkitChangeDetectionRef } from '@shared/abstraction/appkit-change-detection-ref';
import moment from 'moment';
import { timer } from 'rxjs';
import { DefaultFilterComponent } from '../base-components/default-filter.component';

@Component({
  standalone: false,
  selector: 'gstar-datepicker-filter',
  templateUrl: './datepicker-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerFilterComponent
  extends DefaultFilterComponent<string[] | undefined>
  implements OnInit
{
  datePicker: Signal<FieldComponent | undefined> =
    viewChild<FieldComponent>('datepickerField');

  selected: WritableSignal<Date[]> = signal([]);
  previousValue: WritableSignal<Date[]> = signal([]);
  hasError: WritableSignal<boolean> = signal(false);

  constructor() {
    super();
    this.clearAll.subscribe(() => {
      this.clearValue();
      this.previousValue.set([]);
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.value()) {
      const dates = this.parseToDate(this.value()!);
      this.selected.set(dates);
    }
  }

  updateDateHandler(updatedDate: Date[]): void {
    if (updatedDate.length === 2 && this.valueHasChange(updatedDate)) {
      this.previousValue.set(updatedDate);
      this.update([
        this.parseToISO8601(updatedDate[0], true),
        this.parseToISO8601(updatedDate[1], false),
      ]);
    } else if (updatedDate.length === 0 && this.valueHasChange(updatedDate)) {
      this.previousValue.set(updatedDate);
      this.update(undefined);
    }
  }

  invalidDateHandler(event: DatepickerOnDateInvalid): void {
    this.hasError.set(event.invalid);
  }

  showHandler(_event: Event): void {
    timer(0).subscribe(() => {
      this.utils
        .parseTo<AppkitChangeDetectionRef>(this.datePicker())
        .cdr.detectChanges();
    });
  }

  private clearValue(): void {
    this.selected.set([]);
    if (this.datePicker()) {
      this.datePicker()?.inputRef.writeValue('');
      this.hasError.set(false);
    }
  }

  private parseToDate(datesStrings: string[]): Date[] {
    return datesStrings.map((isoDate: string) => {
      return new Date(isoDate.replace('Z', ''));
    });
  }

  private parseToISO8601(date: Date, initialTime: boolean): string {
    const time = initialTime
      ? { hour: 0, minute: 0, second: 0 }
      : { hour: 23, minute: 59, second: 59 };
    return moment(date).set(time).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  }

  private valueHasChange(dateList: Date[]): boolean {
    return (
      dateList.length !== this.previousValue().length ||
      moment(this.previousValue()[0]).diff(moment(dateList[0]), 'days') !== 0 ||
      moment(this.previousValue()[1]).diff(moment(dateList[1]), 'days') !== 0
    );
  }
}
